import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Field, MessageBar, Option, Textarea } from '@fluentui/react-components';
import IConnectToTeam from '../../../../../model/IConnectToTeam';
import { useAppDispatch } from '../../../../../store/hooks';
import { createWelcomeMessages } from '../../../../../store/features/welcomeMessages/welcomeMessagesSlice';
import BulkAddDialog, {
    BulkAddDialogResponseMessage,
} from '../../../components/BulkAddDialog/BulkAddDialog';

export interface BulkAddAircraftRegistrationsDialogProps {
    open: boolean;
    onClose: () => void;
    groundTeams: IConnectToTeam[];
    groundTeamLoadStatus: string;
}

interface BulkAddAircraftRegistrationsResponse {
    successCount: number;
    duplicateCount: number;
    failureCount: number;
    failures: Array<{
        registration: string;
        reason: string;
    }>;
}

const MAX_ITEMS = 30;

const BulkAddAircraftRegistrationsDialog: FC<BulkAddAircraftRegistrationsDialogProps> = ({
    open,
    onClose,
    groundTeams,
    groundTeamLoadStatus,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [selectedGroundTeamRowKey, setSelectedGroundTeamRowKey] = useState('');
    const [autoConnectionMessage, setAutoConnectionMessage] = useState('');

    useEffect(() => {
        if (!open) {
            setSelectedGroundTeamRowKey('');
            setAutoConnectionMessage('');
        }
    }, [open]);

    const selectedGroundTeam = useMemo(
        () => groundTeams.find((team) => team.key === selectedGroundTeamRowKey),
        [groundTeams, selectedGroundTeamRowKey]
    );
    const isAutoConnectionMessageEmpty = !autoConnectionMessage.trim();

    const submitItems = useCallback(
        async (items: string[]): Promise<BulkAddAircraftRegistrationsResponse> => {
            if (!selectedGroundTeamRowKey) {
                throw new Error(t('Dialog_BulkAddRegistrations_GroundTeamRequired'));
            }

            if (isAutoConnectionMessageEmpty) {
                throw new Error(
                    t('Dialog_BulkAddRegistrations_MessageRequired', {
                        defaultValue: 'Please enter an auto connection message.',
                    })
                );
            }

            const result: BulkAddAircraftRegistrationsResponse = {
                successCount: 0,
                duplicateCount: 0,
                failureCount: 0,
                failures: [],
            };

            const message = autoConnectionMessage.trim();

            for (const registration of items) {
                const normalizedRegistration = registration.trim().toUpperCase();

                try {
                    await dispatch(
                        createWelcomeMessages({
                            rowKey: normalizedRegistration,
                            value: '',
                            autoConnectionEnabled: true,
                            autoConnectionMessage: message,
                            autoConnectionGroundTeamRowKey: selectedGroundTeamRowKey,
                        })
                    ).unwrap();
                    result.successCount += 1;
                } catch (error) {
                    result.failureCount += 1;
                    result.failures.push({
                        registration: normalizedRegistration,
                        reason: error instanceof Error ? error.message : String(error),
                    });
                }
            }

            return result;
        },
        [dispatch, selectedGroundTeamRowKey, autoConnectionMessage, isAutoConnectionMessageEmpty, t]
    );

    const getResponseMessage = useCallback(
        (result: BulkAddAircraftRegistrationsResponse): BulkAddDialogResponseMessage | null => {
            if (
                result.successCount === 0 &&
                result.duplicateCount === 0 &&
                result.failureCount === 0
            ) {
                return null;
            }

            const messageKey =
                result.failureCount === 0
                    ? 'Dialog_BulkAddRegistrations_SuccessMessage'
                    : 'Dialog_BulkAddRegistrations_PartialSuccessMessage';

            return {
                type: result.failureCount > 0 ? 'warning' : 'success',
                message: t(messageKey, {
                    successCount: result.successCount,
                    failedCount: result.failureCount,
                    duplicateCount: result.duplicateCount,
                }),
            };
        },
        [t]
    );

    const renderResponseDetails = useCallback(
        (result: BulkAddAircraftRegistrationsResponse) => {
            if (result.failures.length === 0) {
                return null;
            }

            return (
                <div>
                    <div>
                        <strong>{t('Dialog_BulkAddRegistrations_FailuresLabel')}</strong>
                    </div>
                    <div>
                        {result.failures.map((failure) => (
                            <div key={failure.registration}>
                                {failure.registration}: {failure.reason}
                            </div>
                        ))}
                    </div>
                </div>
            );
        },
        [t]
    );

    return (
        <BulkAddDialog<BulkAddAircraftRegistrationsResponse>
            open={open}
            title={t('Dialog_BulkAddRegistrations_Title')}
            children={
                <>
                    <Field
                        label={t('Dialog_BulkAddRegistrations_GroundTeamLabel')}
                        required
                        validationState={!selectedGroundTeamRowKey ? 'error' : undefined}
                        validationMessage={
                            !selectedGroundTeamRowKey
                                ? t('Dialog_BulkAddRegistrations_GroundTeamRequired')
                                : undefined
                        }
                    >
                        <Dropdown
                            value={
                                selectedGroundTeam
                                    ? `${selectedGroundTeam.teamName} - ${selectedGroundTeam.channelName}`
                                    : ''
                            }
                            placeholder={t('Dialog_BulkAddRegistrations_GroundTeamPlaceholder')}
                            disabled={groundTeamLoadStatus !== 'idle'}
                            onOptionSelect={(_, data) => {
                                setSelectedGroundTeamRowKey(data.optionValue ?? '');
                            }}
                        >
                            {groundTeams.map((team) => (
                                <Option key={team.key} value={team.key}>
                                    {`${team.teamName} - ${team.channelName}`}
                                </Option>
                            ))}
                        </Dropdown>
                    </Field>
                    <Field
                        label={t('Dialog_BulkAddRegistrations_MessageLabel', {
                            defaultValue: 'Auto connection message',
                        })}
                        required
                        validationState={isAutoConnectionMessageEmpty ? 'error' : undefined}
                        validationMessage={
                            isAutoConnectionMessageEmpty
                                ? t('Dialog_BulkAddRegistrations_MessageRequired', {
                                      defaultValue: 'Please enter an auto connection message.',
                                  })
                                : undefined
                        }
                    >
                        <Textarea
                            value={autoConnectionMessage}
                            onChange={(_, data) => {
                                setAutoConnectionMessage(data.value);
                            }}
                            resize="vertical"
                            rows={5}
                        />
                    </Field>
                </>
            }
            footerContent={
                selectedGroundTeamRowKey && !isAutoConnectionMessageEmpty ? (
                    <MessageBar intent="warning">
                        {t('Dialog_BulkAddRegistrations_AutoConnectionWarning', {
                            defaultValue:
                                'Auto connection will be turned on for all aircraft registrations entered below.',
                        })}
                    </MessageBar>
                ) : undefined
            }
            fieldLabel={t('Dialog_BulkAddRegistrations_FieldLabel')}
            fieldHint={t('Dialog_BulkAddRegistrations_FieldHint')}
            processingLabel={t('Dialog_BulkAddRegistrations_Processing')}
            saveButtonLabel={t('Button_Common_Save')}
            countBadgeLabel={(count) => t('Dialog_BulkAddRegistrations_CountBadge', { count })}
            duplicateItemsLabel={(count) => t('Dialog_BulkAddRegistrations_DuplicatesBar', { count })}
            invalidItemsLabel={(count) => t('Dialog_BulkAddRegistrations_InvalidBar', { count })}
            maxItems={MAX_ITEMS}
            itemPattern={/^G[A-Z]{0,4}$/i}
            maxItemsErrorMessage={t('Dialog_BulkAddRegistrations_MaxItemsExceeded', {
                max: MAX_ITEMS,
            })}
            invalidMessage={t('Dialog_BulkAddRegistrations_InvalidErrMsg')}
            isSubmitDisabled={!selectedGroundTeamRowKey || isAutoConnectionMessageEmpty}
            onClose={onClose}
            submitItems={submitItems}
            getResponseMessage={getResponseMessage}
            renderResponseDetails={renderResponseDetails}
        />
    );
};

export default BulkAddAircraftRegistrationsDialog;
